import { useState, useEffect } from "react";
import { AnimatePresence, motion } from "framer-motion";
import OwnerHeader from "./OwnerHeader";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Save, Calendar, Settings as SettingsIcon, AlertCircle, Edit, X } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { db, auth } from "@/firebase/config";
import { doc, getDoc, setDoc } from "firebase/firestore";
import { onAuthStateChanged } from "firebase/auth";
import OwnerSidebar from "./OwnerSidebar";
import { SidebarProvider } from "@/components/ui/sidebar";
import { Alert, AlertDescription } from "@/components/ui/alert";
import ContentLoader from "../authentication/ContentLoader";

interface TermDates {
  term1Start: string;
  term1End: string;
  term2Start: string;
  term2End: string;
  term3Start: string;
  term3End: string;
}

interface SystemConfig {
  termDates: TermDates;
  updatedAt: string;
  updatedBy: string;
}


const SystemConfiguration = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isOwner, setIsOwner] = useState(false);
  const [currentUser, setCurrentUser] = useState<any>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [colorMode, setColorMode] = useState(() => {
    if (typeof window !== 'undefined') {
      return document.documentElement.classList.contains('dark') ? 'dark' : 'light';
    }
    return 'light';
  });

  // Listen for dark mode changes
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const html = document.documentElement;
    const observer = new window.MutationObserver(() => {
      setColorMode(html.classList.contains('dark') ? 'dark' : 'light');
    });
    observer.observe(html, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const [termDates, setTermDates] = useState<TermDates>({
    term1Start: "",
    term1End: "",
    term2Start: "",
    term2End: "",
    term3Start: "",
    term3End: "",
  });

  // Authentication check
  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        toast({
          title: "Authentication Required",
          description: "Please log in as owner to access system configuration.",
          variant: "destructive",
          duration: 5000,
        });
        window.location.href = "/login-page";
        return;
      }

      try {
        const idTokenResult = await user.getIdTokenResult(true);
        const claims = idTokenResult.claims;

        // Check if user has owner role
        const affiliations = (claims.affiliations as any[]) || [];
        const defaultAff = claims.defaultAffiliation as any;
        
        const hasOwnerRole = defaultAff?.role === "owner" || 
                            affiliations.some(aff => aff.role === "owner");

        if (!hasOwnerRole) {
          toast({
            title: "Access Denied",
            description: "You don't have permission to access system configuration.",
            variant: "destructive",
            duration: 5000,
          });
          window.location.href = "/owner-dashboard";
          return;
        }

        setIsOwner(true);
        setCurrentUser(user);
        await loadSystemConfig();
      } catch (error) {
        console.error("Error verifying owner access:", error);
        toast({
          title: "Error",
          description: "Failed to verify access. Please try again.",
          variant: "destructive",
          duration: 5000,
        });
      } finally {
        setLoading(false);
      }
    });

    return () => unsubscribe();
  }, [toast]);

  // Load existing system configuration
  const loadSystemConfig = async () => {
    try {
      const configRef = doc(db, "systemConfig/global");
      const configSnap = await getDoc(configRef);

      if (configSnap.exists()) {
        const data = configSnap.data() as SystemConfig;
        setTermDates(data.termDates || {
          term1Start: "",
          term1End: "",
          term2Start: "",
          term2End: "",
          term3Start: "",
          term3End: "",
        });
        // If data exists, disable editing by default
        setIsEditing(false);
      } else {
        // If no data exists, enable editing
        setIsEditing(true);
      }
    } catch (error) {
      console.error("Error loading system config:", error);
      toast({
        title: "Error",
        description: "Failed to load system configuration.",
        variant: "destructive",
        duration: 3000,
      });
    }
  };

  // Save term dates
  const saveTermDates = async () => {
    if (!currentUser) return;

    // Validate dates
    if (!termDates.term1Start || !termDates.term1End ||
        !termDates.term2Start || !termDates.term2End ||
        !termDates.term3Start || !termDates.term3End) {
      toast({
        title: "Validation Error",
        description: "Please fill in all term dates.",
        variant: "destructive",
        duration: 3000,
      });
      return;
    }

    setSaving(true);
    try {
      const configRef = doc(db, "systemConfig/global");
      const configData: SystemConfig = {
        termDates,
        updatedAt: new Date().toISOString(),
        updatedBy: currentUser.uid,
      };

      await setDoc(configRef, configData, { merge: true });

      toast({
        title: "Success",
        description: "Term dates saved successfully.",
        duration: 3000,
      });
      
      // Disable editing after successful save
      setIsEditing(false);
    } catch (error) {
      console.error("Error saving term dates:", error);
      toast({
        title: "Error",
        description: "Failed to save term dates. Please try again.",
        variant: "destructive",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };



  if (!isOwner) {
    return null;
  }


  // Use colorMode for framer-motion key

  return (
    <SidebarProvider>
      <div className="flex h-screen w-full bg-gray-50 text-gray-900 dark:bg-gray-900 dark:text-gray-100">
        <OwnerSidebar userRole="owner" activeSection="system-config" onSectionChange={() => {}} />
        <div className="flex flex-col flex-1 h-full">
          <OwnerHeader />
          <AnimatePresence mode="wait">
            <motion.main
              key={colorMode}
              initial={{ opacity: 0, filter: 'blur(8px)' }}
              animate={{ opacity: 1, filter: 'blur(0px)' }}
              exit={{ opacity: 0, filter: 'blur(8px)' }}
              transition={{ duration: 0.4, ease: 'easeInOut' }}
              className="flex-1 overflow-y-auto min-h-0 p-3 sm:p-6"
            >
              <ContentLoader isLoading={loading} message="">

            <Alert className="mb-6 border-blue-200 bg-blue-50 dark:bg-blue-950/50 dark:border-blue-800">
              <AlertCircle className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <AlertDescription className="text-blue-800 dark:text-blue-200">
                These settings apply to all schools in the system. Changes will affect all users.
              </AlertDescription>
            </Alert>

            <Tabs defaultValue="terms" className="space-y-6">
              <TabsList className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-1">
                <TabsTrigger 
                  value="terms" 
                  className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                >
                  <Calendar className="h-4 w-4" />
                  Term Dates
                </TabsTrigger>
                <TabsTrigger 
                  value="other" 
                  className="flex items-center gap-2 data-[state=active]:bg-blue-600 data-[state=active]:text-white"
                >
                  <SettingsIcon className="h-4 w-4" />
                  Other Settings
                </TabsTrigger>
              </TabsList>

              {/* Term Dates Tab */}
              <TabsContent value="terms">
                <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600">
                  <CardHeader>
                    <div className="flex items-center justify-between">
                      <div>
                        <CardTitle className="text-gray-900 dark:text-white">Academic Term Dates</CardTitle>
                        <CardDescription className="text-gray-600 dark:text-gray-400">
                          Set the start and end dates for each term. Use MM-DD format (e.g., 01-15 for January 15). These dates repeat annually.
                        </CardDescription>
                      </div>
                      {!isEditing && (
                        <Button
                          onClick={() => setIsEditing(true)}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2"
                        >
                          <Edit className="h-4 w-4" />
                          Edit
                        </Button>
                      )}
                      {isEditing && (
                        <Button
                          onClick={() => {
                            setIsEditing(false);
                            loadSystemConfig(); // Reload to cancel changes
                          }}
                          variant="outline"
                          size="sm"
                          className="flex items-center gap-2 text-red-600 hover:text-red-700 border-red-300 hover:border-red-400"
                        >
                          <X className="h-4 w-4" />
                          Cancel
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="p-6">
                    {/* Term 1 */}
                    <div className="m-3 p-5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                        Term 1
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="term1Start" className="text-gray-700 dark:text-gray-300 font-semibold">Start Date (MM-DD)</Label>
                          <Input
                            id="term1Start"
                            type="text"
                            value={termDates.term1Start}
                            onChange={(e) =>
                              setTermDates({ ...termDates, term1Start: e.target.value })
                            }
                            placeholder="01-05"
                            disabled={!isEditing}
                            className="mt-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <Label htmlFor="term1End" className="text-gray-700 dark:text-gray-300 font-semibold">End Date (MM-DD)</Label>
                          <Input
                            id="term1End"
                            type="text"
                            value={termDates.term1End}
                            onChange={(e) =>
                              setTermDates({ ...termDates, term1End: e.target.value })
                            }
                            placeholder="04-15"
                            disabled={!isEditing}
                            className="mt-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Term 2 */}
                    <div className="m-3 p-5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                        Term 2
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="term2Start" className="text-gray-700 dark:text-gray-300 font-semibold">Start Date (MM-DD)</Label>
                          <Input
                            id="term2Start"
                            type="text"
                            value={termDates.term2Start}
                            onChange={(e) =>
                              setTermDates({ ...termDates, term2Start: e.target.value })
                            }
                            placeholder="05-10"
                            disabled={!isEditing}
                            className="mt-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <Label htmlFor="term2End" className="text-gray-700 dark:text-gray-300 font-semibold">End Date (MM-DD)</Label>
                          <Input
                            id="term2End"
                            type="text"
                            value={termDates.term2End}
                            onChange={(e) =>
                              setTermDates({ ...termDates, term2End: e.target.value })
                            }
                            placeholder="08-20"
                            disabled={!isEditing}
                            className="mt-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Term 3 */}
                    <div className="m-3 p-5 border border-gray-200 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                      <h3 className="text-lg font-bold mb-4 text-gray-900 dark:text-white">
                        Term 3
                      </h3>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                        <div>
                          <Label htmlFor="term3Start" className="text-gray-700 dark:text-gray-300 font-semibold">Start Date (MM-DD)</Label>
                          <Input
                            id="term3Start"
                            type="text"
                            value={termDates.term3Start}
                            onChange={(e) =>
                              setTermDates({ ...termDates, term3Start: e.target.value })
                            }
                            placeholder="09-05"
                            disabled={!isEditing}
                            className="mt-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                        </div>
                        <div>
                          <Label htmlFor="term3End" className="text-gray-700 dark:text-gray-300 font-semibold">End Date (MM-DD)</Label>
                          <Input
                            id="term3End"
                            type="text"
                            value={termDates.term3End}
                            onChange={(e) =>
                              setTermDates({ ...termDates, term3End: e.target.value })
                            }
                            placeholder="11-30"
                            disabled={!isEditing}
                            className="mt-2 bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white disabled:opacity-60 disabled:cursor-not-allowed"
                          />
                        </div>
                      </div>
                    </div>

                    {/* Save Button */}
                    <div className="flex justify-end pt-6 mt-6 border-t border-gray-200 dark:border-gray-600">
                      <Button
                        onClick={saveTermDates}
                        disabled={saving || !isEditing}
                        className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-600 dark:hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {saving ? (
                          <>
                            <div className="animate-spin rounded-full h-4 w-4 border-t-2 border-b-2 border-white mr-2"></div>
                            Saving...
                          </>
                        ) : (
                          <>
                            <Save className="h-4 w-4 mr-2" />
                            Save Term Dates
                          </>
                        )}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>

              {/* Other Settings Tab */}
              <TabsContent value="other">
                <Card className="bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-600">
                  <CardHeader>
                    <CardTitle className="text-gray-900 dark:text-white">Additional System Settings</CardTitle>
                    <CardDescription className="text-gray-600 dark:text-gray-400">
                      Configure other system-wide parameters
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="text-center py-12 text-gray-500 dark:text-gray-400">
                      <SettingsIcon className="h-12 w-12 mx-auto mb-4 opacity-50" />
                      <p className="font-semibold">Additional settings will be added here.</p>
                      <p className="text-sm mt-2">
                        This section is reserved for future system-wide configurations.
                      </p>
                    </div>
                  </CardContent>
                </Card>
              </TabsContent>
            </Tabs>
            </ContentLoader>
            </motion.main>
          </AnimatePresence>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default SystemConfiguration;
